<template>
    <div class="bg-white shadow-sm p-4">
        <div class="modal-header d-flex flex-column">
            <h5 class="modal-title font-weight-400">
                Payment Settings
            </h5>
            <p class="d-flex flex-row align-items-center mb-0">
                <span class="text-muted mb-0">
                    Configure the payment settings for your biller.
                </span>
            </p>
        </div>
        <div class="row p-4">
            <div class="col-md-12">
                <!-- Fixed Fees -->
                <div class="mb-4">
                    <h6 class="font-weight-bold">Fixed Fees</h6>
                    <div class="form-group">
                        <div class="custom-control custom-switch">
                            <input type="checkbox" class="custom-control-input" id="fixedFeeUSDSwitch" v-model="settings.pass_fixed_fee_usd">
                            <label class="custom-control-label" for="fixedFeeUSDSwitch">Pass USD Fixed Fee to Customer</label>
                            <small class="form-text text-muted d-block">
                                {{ fixedFeeUSDDescription }}
                            </small>
                        </div>
                    </div>
                    <div class="form-group">
                        <div class="custom-control custom-switch">
                            <input type="checkbox" class="custom-control-input" id="fixedFeeZWGSwitch" v-model="settings.pass_fixed_fee_zwg">
                            <label class="custom-control-label" for="fixedFeeZWGSwitch">Pass ZWG Fixed Fee to Customer</label>
                            <small class="form-text text-muted d-block">
                                {{ fixedFeeZWGDescription }}
                            </small>
                        </div>
                    </div>
                </div>

                <!-- Service Fees -->
                <div class="mb-4">
                    <h6 class="font-weight-bold">Service Fees</h6>
                    <div class="form-group">
                        <div class="custom-control custom-switch">
                            <input type="checkbox" class="custom-control-input" id="serviceFeeUSDSwitch" v-model="settings.pass_service_fee_usd">
                            <label class="custom-control-label" for="serviceFeeUSDSwitch">Pass USD Service Fee to Customer</label>
                            <small class="form-text text-muted d-block">
                                {{ serviceFeeUSDDescription }}
                            </small>
                        </div>
                    </div>
                    <div class="form-group">
                        <div class="custom-control custom-switch">
                            <input type="checkbox" class="custom-control-input" id="serviceFeeZWGSwitch" v-model="settings.pass_service_fee_zwg">
                            <label class="custom-control-label" for="serviceFeeZWGSwitch">Pass ZWG Service Fee to Customer</label>
                            <small class="form-text text-muted d-block">
                                {{ serviceFeeZWGDescription }}
                            </small>
                        </div>
                    </div>
                </div>

                <!-- Payment Processing Fee -->
                <div class="mb-4">
                    <h6 class="font-weight-bold">Payment Processing Fee</h6>
                    <div class="form-group">
                        <div class="custom-control custom-switch">
                            <input type="checkbox" class="custom-control-input" id="processingFeeSwitch" v-model="settings.pass_payment_processing_fee">
                            <label class="custom-control-label" for="processingFeeSwitch">Pass Payment Processing Fee to Customer</label>
                            <small class="form-text text-muted d-block">
                                {{ processingFeeDescription }}
                            </small>
                        </div>
                    </div>
                </div>

                <div class="form-group mt-4 w-50">
                    <button class="btn btn-primary btn-block" @click="saveSettings" :disabled="loading">
                        <span v-if="loading" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                        Save
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Form from "../../../../core/forms/form";

export default {
    name: "PaymentSettings",
    props: {
        corporate: {
            type: Number,
            required: true
        }
    },
    data() {
        return {
            loading: false,
            settings: {
                pass_fixed_fee_usd: false,
                pass_fixed_fee_zwg: false,
                pass_service_fee_usd: false,
                pass_service_fee_zwg: false,
                pass_payment_processing_fee: false
            },
            feeValues: {
                fixedFeeUSD: null,
                fixedFeeZWG: null,
                serviceFeeUSD: null,
                serviceFeeZWG: null,
                processingFeeUSD: null,
                processingFeeZWG: null
            },
            feeDescriptions: {
                fixedFeeUSD: '',
                fixedFeeZWG: '',
                serviceFeeUSD: '',
                serviceFeeZWG: '',
                processingFee: ''
            }
        }
    },
    computed: {
        fixedFeeUSDDescription() {
            if (!this.feeDescriptions.fixedFeeUSD) return '';
            return `${this.feeDescriptions.fixedFeeUSD} (USD ${this.feeValues.fixedFeeUSD} per transaction)`;
        },
        fixedFeeZWGDescription() {
            if (!this.feeDescriptions.fixedFeeZWG) return '';
            return `${this.feeDescriptions.fixedFeeZWG} (ZWG ${this.feeValues.fixedFeeZWG} per transaction)`;
        },
        serviceFeeUSDDescription() {
            if (!this.feeDescriptions.serviceFeeUSD) return '';
            return `${this.feeDescriptions.serviceFeeUSD} (${this.feeValues.serviceFeeUSD}% per transaction)`;
        },
        serviceFeeZWGDescription() {
            if (!this.feeDescriptions.serviceFeeZWG) return '';
            return `${this.feeDescriptions.serviceFeeZWG} (${this.feeValues.serviceFeeZWG}% per transaction)`;
        },
        processingFeeDescription() {
            if (!this.feeDescriptions.processingFee) return '';
            const currency = this.corporateCurrency === 'USD' ? 'USD' : 'ZWG';
            const value = this.corporateCurrency === 'USD' ? this.feeValues.processingFeeUSD : this.feeValues.processingFeeZWG;
            return `${this.feeDescriptions.processingFee} (${value}% per transaction)`;
        }
    },
    mounted() {
        this.loadSettings();
        this.loadFeeValues();
    },
    methods: {
        loadSettings() {
            this.loading = true;
            window.axios.get(`/business/biller-onboarding/payment-settings/${this.corporate}`)
                .then(response => {
                    if (response.data.body) {
                        this.settings = response.data.body;
                    }
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        loadFeeValues() {
            window.axios.get('/business/biller-onboarding/fee-variables')
                .then(response => {
                    const systemVars = response.data;
                    // Load fixed fees
                    this.feeValues.fixedFeeUSD = systemVars['BILLER-FIXED-FEE-USD']?.value || 0;
                    this.feeValues.fixedFeeZWG = systemVars['BILLER-FIXED-FEE-ZWG']?.value || 0;
                    this.feeDescriptions.fixedFeeUSD = systemVars['BILLER-FIXED-FEE-USD']?.description || '';
                    this.feeDescriptions.fixedFeeZWG = systemVars['BILLER-FIXED-FEE-ZWG']?.description || '';

                    // Load service fees
                    this.feeValues.serviceFeeUSD = systemVars['BILLER-SERVICE-FEE']?.value || 0;
                    this.feeValues.serviceFeeZWG = systemVars['BILLER-SERVICE-FEE-ZWG']?.value || 0;
                    this.feeDescriptions.serviceFeeUSD = systemVars['BILLER-SERVICE-FEE']?.description || '';
                    this.feeDescriptions.serviceFeeZWG = systemVars['BILLER-SERVICE-FEE-ZWG']?.description || '';

                    // Load processing fees
                    this.feeValues.processingFeeUSD = systemVars['BILLER-PAYMENT-PROCESSING-FEE']?.value || 0;
                    this.feeValues.processingFeeZWG = systemVars['BILLER-PAYMENT-PROCESSING-FEE-ZWG']?.value || 0;
                    this.feeDescriptions.processingFee = systemVars['BILLER-PAYMENT-PROCESSING-FEE']?.description || '';
                });
        },
        saveSettings() {
            this.loading = true;
            window.axios.post(`/business/biller-onboarding/payment-settings/${this.corporate}`, this.settings)
                .then(response => {
                    this.$toast.success('Settings saved successfully');
                })
                .catch(error => {
                    this.$toast.error('Failed to save settings');
                })
                .finally(() => {
                    this.loading = false;
                });
        }
    }
}
</script>

<style scoped>
.custom-switch {
    padding-left: 2.25rem;
    margin-bottom: 1rem;
}
</style>